import React, { useEffect, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import {
    Button,
    Grid,
    Card,
    Container as MaterialContainer,
} from '@sunwisesoftware/sunwise-ui';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import {
    TitleIcon,
    Modal,
    StatusFilterSelect,
    HeaderContainer,
} from 'common/components';
import { DEBOUNCE_TIME_500 } from 'common/constants';
import { PERMISSION_LIST } from 'common/constants/permissions';
import withPermissions from 'common/hocs/withPermissions';

import * as actions from './actions';
import {
    CardList,
    CoverageConfigForm,
    CoverageConfigPreview,
    CoverageForm,
} from './components';
import * as selectors from './selectors';

const Container = ({
    canModify,
    coverages,
    fetchCoverages,
    handleClickCoverageFormModal,
    handleToggleCoverageConfigModal,
    initialValuesCoverageConfigForm,
    isFetchingCoverages,
    isOpenCoverageConfigModal,
    isOpenModal,
    prepareArchiveCoverage,
    prepareDeleteCoverageConfig,
    prepareUpdateCoverageConfig,
}) => {
    const { t } = useTranslation();
    const [selectedCoverage, setSelectedCoverage] = useState(null);
    const [filterData, setFilterData] = useState({ status: 'active' });

    const [currentFilterData, setCurrentFilterData] = useState({});

    useEffect(() => {
        if (!isEqual(currentFilterData, filterData)) {
            setCurrentFilterData(filterData);
            handleOnFilter(filterData);
        }
    }, [filterData]);

    const handleOnFilter = useRef(
        debounce((filter) => fetchCoverages(filter), DEBOUNCE_TIME_500),
    ).current;

    const hanldeOnClickCoverageConfigPrepareUpdate = (item) =>
        prepareUpdateCoverageConfig(item);

    const handleOnSelectStatus = (value) => {
        const filter = { ...filterData, page: 0, status: value };
        setFilterData(filter);
    };

    return (
        <React.Fragment>
            <MaterialContainer maxWidth={false}>
                <HeaderContainer>
                    <TitleIcon title={t('Coverage')} />
                </HeaderContainer>
                <Grid container>
                    <Grid size={{ md: 9 }}>
                        <Card sx={{ minHeight: 400 }}>
                            <Card.Header>
                                <Grid container sx={{ alignItems: 'center' }}>
                                    <Grid size={{ lg: 6, md: 9, xs: 18 }}>
                                        <TitleIcon
                                            fontWeight="bold"
                                            title={t('Operating costs')}
                                            variant="subtitle"
                                        />
                                    </Grid>
                                    <Grid size={{ lg: 4, md: 9, xs: 18 }}>
                                        <StatusFilterSelect
                                            filterBy={filterData?.status}
                                            handleSelected={
                                                handleOnSelectStatus
                                            }
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid
                                        hidden={!canModify}
                                        size={{ xs: 'grow' }}
                                        textAlign="right"
                                    >
                                        <Button
                                            endIcon={
                                                <AddIcon fontSize="small" />
                                            }
                                            onClick={() =>
                                                handleClickCoverageFormModal(
                                                    true,
                                                )
                                            }
                                        >
                                            {t('Add')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Card.Header>

                            <CardList
                                canModify={canModify}
                                coverages={coverages}
                                filterData={filterData}
                                isFetching={isFetchingCoverages}
                                prepareArchiveCoverage={prepareArchiveCoverage}
                                selectedCoverage={selectedCoverage}
                                setSelectedCoverage={setSelectedCoverage}
                            />
                        </Card>
                    </Grid>

                    <Grid size={{ md: 9 }}>
                        <CoverageConfigPreview
                            canModify={canModify}
                            filterData={filterData}
                            handleToggleCoverageConfigModal={
                                handleToggleCoverageConfigModal
                            }
                            prepareDelete={prepareDeleteCoverageConfig}
                            prepareUpdate={
                                hanldeOnClickCoverageConfigPrepareUpdate
                            }
                            selectedCoverage={selectedCoverage}
                            setSelectedCoverage={setSelectedCoverage}
                        />
                    </Grid>
                </Grid>

                <Modal
                    keyboard={false}
                    onHide={() => handleClickCoverageFormModal(false)}
                    show={isOpenModal}
                    size="sm"
                    title={t('New operating cost')}
                >
                    <CoverageForm filterData={filterData} />
                </Modal>

                <Modal
                    keyboard={false}
                    onHide={() => handleToggleCoverageConfigModal(false)}
                    show={isOpenCoverageConfigModal}
                    size="sm"
                    title={`${
                        initialValuesCoverageConfigForm.id === null
                            ? t('Create')
                            : t('Update')
                    } ${t('Configuration')}`}
                >
                    <CoverageConfigForm
                        filterData={filterData}
                        selectedCoverage={selectedCoverage}
                        setSelectedCoverage={setSelectedCoverage}
                    />
                </Modal>
            </MaterialContainer>
        </React.Fragment>
    );
};

Container.propTypes = {
    canModify: PropTypes.bool,
    coverages: PropTypes.array,
    fetchCoverages: PropTypes.func,
    handleClickCoverageFormModal: PropTypes.func,
    handleToggleCoverageConfigModal: PropTypes.func,
    initialValuesCoverageConfigForm: PropTypes.object,
    isFetchingCoverages: PropTypes.bool,
    isOpenCoverageConfigModal: PropTypes.bool,
    isOpenModal: PropTypes.bool,
    prepareArchiveCoverage: PropTypes.func,
    prepareDeleteCoverageConfig: PropTypes.func,
    prepareUpdateCoverageConfig: PropTypes.func,
};

const mapStateToProps = createStructuredSelector({
    coverages: selectors.getCoveragesData,
    isOpenCoverageConfigModal: selectors.getIsOpenCoverageConfigModal,
    isOpenModal: selectors.getIsOpenCoverageFormModal,
    initialValuesCoverageConfigForm:
        selectors.getInitialValuesCoverageConfigForm,
    isFetchingCoverages: selectors.getCoveragesIsFetching,
});

const mapDispatchToProps = (dispatch) => ({
    fetchCoverages: (filterData) =>
        dispatch(actions.fetchCoverages(filterData)),
    handleClickCoverageFormModal: (value) =>
        dispatch(actions.toggleCoverageModal(value)),
    handleToggleCoverageConfigModal: (value) =>
        dispatch(actions.toggleCoverageConfigModal(value)),
    prepareArchiveCoverage: (filterData, item, value, callback) =>
        dispatch(
            actions.prepareArchiveCoverage(filterData, item, value, callback),
        ),
    prepareDeleteCoverageConfig: (filterData, item, setSelectedCoverage) =>
        dispatch(
            actions.prepareDeleteCoverageConfig(
                filterData,
                item,
                setSelectedCoverage,
            ),
        ),
    prepareUpdateCoverageConfig: (item) =>
        dispatch(actions.prepareUpdateCoverageConfig(item)),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    withPermissions(PERMISSION_LIST.INSURANCE_DOCUMENTS_PERMISSION),
)(Container);
